import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  LpsActivityApiModel,
  LpsActivityResourceApiModel,
  LpsWeeklyPlanApiModel,
  LpsWeeklyPlanHeaderApiModel,
  LpsConstraintApiModel,
  LpsFailureReasonApiModel,
  LpsProductionMeetingApiModel,
  LpsMeetingAttendeeApiModel,
  LpsMeetingTopicApiModel,
  LpsPullPlanApiModel,
  LpsPullPlanTaskApiModel,
  LpsResponsibleApiModel,
} from '../models/api/lps.model';
import { environment } from 'environments/environment';
import { getISOWeekNumber } from '../../utils/date.utils';

@Injectable({ providedIn: 'root' })
export class LpsService {
  private base = `${environment.apiEndPoint}/Lps`;

  constructor(private http: HttpClient) {}

  // ============ ACTIVITIES ============

  getActivities(projectId: number, statusId?: number, startDate?: Date, endDate?: Date)
    : Observable<LpsActivityApiModel[]> {
    let params = new HttpParams().set('projectId', projectId.toString());
    if (statusId != null) params = params.set('statusId', statusId.toString());
    if (startDate) params = params.set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());
    return this.http.get<LpsActivityApiModel[]>(`${this.base}/activities`, { params });
  }

  getActivityById(id: number): Observable<LpsActivityApiModel> {
    return this.http.get<LpsActivityApiModel>(`${this.base}/activities/${id}`);
  }

  createActivity(model: LpsActivityApiModel): Observable<LpsActivityApiModel> {
    return this.http.post<LpsActivityApiModel>(`${this.base}/activities`, model);
  }

  updateActivity(id: number, model: LpsActivityApiModel): Observable<LpsActivityApiModel> {
    return this.http.put<LpsActivityApiModel>(`${this.base}/activities/${id}`, model);
  }

  deleteActivity(id: number): Observable<any> {
    return this.http.delete(`${this.base}/activities/${id}`);
  }

  // ============ ACTIVITY RESOURCES (proyectos de diseño) ============

  getActivityResources(activityId: number): Observable<LpsActivityResourceApiModel[]> {
    return this.http.get<LpsActivityResourceApiModel[]>(`${this.base}/activities/${activityId}/resources`);
  }

  addActivityResource(activityId: number, model: LpsActivityResourceApiModel): Observable<LpsActivityResourceApiModel> {
    return this.http.post<LpsActivityResourceApiModel>(`${this.base}/activities/${activityId}/resources`, model);
  }

  updateActivityResource(id: number, model: LpsActivityResourceApiModel): Observable<LpsActivityResourceApiModel> {
    return this.http.put<LpsActivityResourceApiModel>(`${this.base}/activities/resources/${id}`, model);
  }

  deleteActivityResource(id: number): Observable<any> {
    return this.http.delete(`${this.base}/activities/resources/${id}`);
  }

  // ============ LOOKAHEAD ============

  getLookahead(projectId: number): Observable<LpsActivityApiModel[]> {
    const params = new HttpParams().set('projectId', projectId.toString());
    return this.http.get<LpsActivityApiModel[]>(`${this.base}/lookahead`, { params });
  }

  // ============ CONSTRAINTS ============

  addConstraint(model: LpsConstraintApiModel): Observable<LpsConstraintApiModel> {
    return this.http.post<LpsConstraintApiModel>(`${this.base}/constraints`, model);
  }

  getConstraintsLog(projectId: number): Observable<LpsConstraintApiModel[]> {
    const params = new HttpParams().set('projectId', projectId.toString());
    return this.http.get<LpsConstraintApiModel[]>(`${this.base}/constraints/log`, { params });
  }

  releaseConstraint(id: number, userId: number): Observable<LpsConstraintApiModel> {
    return this.http.put<LpsConstraintApiModel>(`${this.base}/constraints/${id}/release`, userId);
  }

  // ============ WEEKLY PLAN ============

  getWeeklyPlan(projectId: number, year?: number, weekNumber?: number): Observable<LpsWeeklyPlanApiModel[]> {
    const now = new Date();
    const y = year ?? now.getFullYear();
    const w = weekNumber ?? getISOWeekNumber(now);
    const params = new HttpParams()
      .set('projectId', projectId.toString())
      .set('year', y.toString())
      .set('weekNumber', w.toString());
    return this.http.get<LpsWeeklyPlanApiModel[]>(`${this.base}/weekly-plan`, { params });
  }

  getWeeklyPlanHeader(projectId: number, year?: number, weekNumber?: number): Observable<LpsWeeklyPlanHeaderApiModel> {
    const now = new Date();
    const y = year ?? now.getFullYear();
    const w = weekNumber ?? getISOWeekNumber(now);
    const params = new HttpParams()
      .set('projectId', projectId.toString())
      .set('year', y.toString())
      .set('weekNumber', w.toString());
    return this.http.get<LpsWeeklyPlanHeaderApiModel>(`${this.base}/weekly-plan/header`, { params });
  }

  updateWeeklyPlanEntry(id: number, model: LpsWeeklyPlanApiModel): Observable<LpsWeeklyPlanApiModel> {
    return this.http.put<LpsWeeklyPlanApiModel>(`${this.base}/weekly-plan/${id}`, model);
  }

  commitActivity(
    activityId: number,
    year?: number,
    weekNumber?: number,
    asCommitted: boolean = false,
    daysOfWeek: string[] = []
  ): Observable<any> {
    const now = new Date();
    const y = year ?? now.getFullYear();
    const w = weekNumber ?? getISOWeekNumber(now);
    const params = new HttpParams()
      .set('activityId', activityId.toString())
      .set('year', y.toString())
      .set('weekNumber', w.toString())
      .set('asCommitted', (asCommitted ?? false).toString());
    return this.http.post(`${this.base}/weekly-plan/commit`, daysOfWeek, { params });
  }

  // ============ RE-PLANNING ============

  replanActivity(weeklyPlanId: number): Observable<LpsActivityApiModel> {
    return this.http.post<LpsActivityApiModel>(`${this.base}/weekly-plan/${weeklyPlanId}/replan`, {});
  }

  // ============ METRICS ============

  getPpc(projectId: number, year?: number, weekNumber?: number): Observable<any> {
    const now = new Date();
    const y = year ?? now.getFullYear();
    const w = weekNumber ?? getISOWeekNumber(now);
    const params = new HttpParams()
      .set('projectId', projectId.toString())
      .set('year', y.toString())
      .set('weekNumber', w.toString());
    return this.http.get(`${this.base}/metrics/ppc`, { params });
  }

  getCncAnalysis(projectId: number, year?: number, weekNumber?: number): Observable<any> {
    const now = new Date();
    const y = year ?? now.getFullYear();
    const w = weekNumber ?? getISOWeekNumber(now);
    const params = new HttpParams()
      .set('projectId', projectId.toString())
      .set('year', y.toString())
      .set('weekNumber', w.toString());
    return this.http.get(`${this.base}/metrics/cnc-pareto`, { params });
  }

  // ============ FAILURE REASONS ============

  getFailureReasons(): Observable<LpsFailureReasonApiModel[]> {
    return this.http.get<LpsFailureReasonApiModel[]>(`${this.base}/failure-reasons`);
  }

  // ============ MASTER PLAN / MILESTONES ============

  getMasterPlan(projectId: number): Observable<LpsActivityApiModel[]> {
    const params = new HttpParams().set('projectId', projectId.toString());
    return this.http.get<LpsActivityApiModel[]>(`${this.base}/master-plan`, { params });
  }

  getMilestones(projectId: number): Observable<LpsActivityApiModel[]> {
    const params = new HttpParams().set('projectId', projectId.toString());
    return this.http.get<LpsActivityApiModel[]>(`${this.base}/milestones`, { params });
  }

  // ============ PRODUCTION MEETINGS ============

  getMeetings(projectId: number, year: number, weekNumber: number): Observable<LpsProductionMeetingApiModel[]> {
    const params = new HttpParams()
      .set('projectId', projectId.toString())
      .set('year', year.toString())
      .set('weekNumber', weekNumber.toString());
    return this.http.get<LpsProductionMeetingApiModel[]>(`${this.base}/meetings`, { params });
  }

  getMeetingById(id: number): Observable<LpsProductionMeetingApiModel> {
    return this.http.get<LpsProductionMeetingApiModel>(`${this.base}/meetings/${id}`);
  }

  createMeeting(model: LpsProductionMeetingApiModel): Observable<LpsProductionMeetingApiModel> {
    return this.http.post<LpsProductionMeetingApiModel>(`${this.base}/meetings`, model);
  }

  updateMeeting(id: number, model: LpsProductionMeetingApiModel): Observable<LpsProductionMeetingApiModel> {
    return this.http.put<LpsProductionMeetingApiModel>(`${this.base}/meetings/${id}`, model);
  }

  deleteMeeting(id: number): Observable<any> {
    return this.http.delete(`${this.base}/meetings/${id}`);
  }

  // Meeting Attendees
  addAttendee(attendee: LpsMeetingAttendeeApiModel): Observable<LpsMeetingAttendeeApiModel> {
    return this.http.post<LpsMeetingAttendeeApiModel>(`${this.base}/meetings/attendees`, attendee);
  }

  removeAttendee(id: number): Observable<any> {
    return this.http.delete(`${this.base}/meetings/attendees/${id}`);
  }

  // Meeting Topics
  addTopic(topic: LpsMeetingTopicApiModel): Observable<LpsMeetingTopicApiModel> {
    return this.http.post<LpsMeetingTopicApiModel>(`${this.base}/meetings/topics`, topic);
  }

  updateTopic(id: number, topic: LpsMeetingTopicApiModel): Observable<LpsMeetingTopicApiModel> {
    return this.http.put<LpsMeetingTopicApiModel>(`${this.base}/meetings/topics/${id}`, topic);
  }

  deleteTopic(id: number): Observable<any> {
    return this.http.delete(`${this.base}/meetings/topics/${id}`);
  }

  // ============ PULL PLANNING ============

  getPullPlans(projectId: number): Observable<LpsPullPlanApiModel[]> {
    const params = new HttpParams().set('projectId', projectId.toString());
    return this.http.get<LpsPullPlanApiModel[]>(`${this.base}/pull-plans`, { params });
  }

  createPullPlan(model: LpsPullPlanApiModel): Observable<LpsPullPlanApiModel> {
    return this.http.post<LpsPullPlanApiModel>(`${this.base}/pull-plans`, model);
  }

  updatePullPlan(id: number, model: LpsPullPlanApiModel): Observable<LpsPullPlanApiModel> {
    return this.http.put<LpsPullPlanApiModel>(`${this.base}/pull-plans/${id}`, model);
  }

  getPullPlanningTasks(pullPlanId: number): Observable<LpsPullPlanTaskApiModel[]> {
    return this.http.get<LpsPullPlanTaskApiModel[]>(`${this.base}/pull-plans/${pullPlanId}/tasks`);
  }

  createPullPlanningTask(model: LpsPullPlanTaskApiModel): Observable<LpsPullPlanTaskApiModel> {
    return this.http.post<LpsPullPlanTaskApiModel>(`${this.base}/pull-plans/tasks`, model);
  }

  updatePullPlanningTask(id: number, model: LpsPullPlanTaskApiModel): Observable<LpsPullPlanTaskApiModel> {
    return this.http.put<LpsPullPlanTaskApiModel>(`${this.base}/pull-plans/tasks/${id}`, model);
  }

  deletePullPlanningTask(id: number): Observable<any> {
    return this.http.delete(`${this.base}/pull-plans/tasks/${id}`);
  }

  // ============ RESPONSABLES (externos) ============

  getResponsibles(projectId?: number): Observable<LpsResponsibleApiModel[]> {
    let params = new HttpParams();
    if (projectId != null) params = params.set('projectId', projectId.toString());
    return this.http.get<LpsResponsibleApiModel[]>(`${this.base}/responsibles`, { params });
  }

  createResponsible(model: Partial<LpsResponsibleApiModel>): Observable<LpsResponsibleApiModel> {
    return this.http.post<LpsResponsibleApiModel>(`${this.base}/responsibles`, model);
  }

  updateResponsible(id: number, model: LpsResponsibleApiModel): Observable<LpsResponsibleApiModel> {
    return this.http.put<LpsResponsibleApiModel>(`${this.base}/responsibles/${id}`, model);
  }

  deleteResponsible(id: number): Observable<any> {
    return this.http.delete(`${this.base}/responsibles/${id}`);
  }
}
